import React, { useState } from 'react';
import { Trophy, ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface TrophyData {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt?: string;
  progress?: number;
  maxProgress?: number;
}

export const TrophiesDropdown: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Données de trophées simulées (à remplacer par un appel API)
  const trophies: TrophyData[] = [
    {
      id: '1',
      name: 'Premier résumé',
      description: 'Générez votre premier résumé',
      icon: '🎯',
      unlockedAt: '2025-01-11T10:30:00Z',
    },
    {
      id: '2',
      name: 'Résumé de vidéo',
      description: 'Résumez une vidéo YouTube',
      icon: '🎬',
      unlockedAt: '2025-01-11T14:15:00Z',
    },
    {
      id: '3',
      name: 'Économiseur de temps',
      description: 'Économisez 1 heure de lecture',
      icon: '⏰',
      progress: 45,
      maxProgress: 60,
    },
    {
      id: '4',
      name: 'Lecteur assidu',
      description: 'Résumez 10 articles',
      icon: '📚',
      progress: 7,
      maxProgress: 10,
    },
    {
      id: '5',
      name: 'Maître des résumés',
      description: 'Résumez 50 contenus',
      icon: '👑',
      progress: 0,
      maxProgress: 50,
    },
  ];

  const unlockedTrophies = trophies.filter(trophy => trophy.unlockedAt);
  const lockedTrophies = trophies.filter(trophy => !trophy.unlockedAt);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
        title="Mes trophées"
      >
        <Trophy className="h-4 w-4" />
        <span className="hidden md:inline">Trophées</span>
        <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
          {unlockedTrophies.length}
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay pour fermer le dropdown */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Mes trophées
              </h3>

              {/* Trophées débloqués */}
              {unlockedTrophies.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    🏆 Débloqués ({unlockedTrophies.length})
                  </h4>
                  <div className="space-y-3">
                    {unlockedTrophies.map((trophy) => (
                      <div
                        key={trophy.id}
                        className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                      >
                        <div className="text-2xl">{trophy.icon}</div>
                        <div className="flex-1">
                          <div className="font-medium text-green-900 dark:text-green-100">
                            {trophy.name}
                          </div>
                          <div className="text-sm text-green-700 dark:text-green-300">
                            {trophy.description}
                          </div>
                          {trophy.unlockedAt && (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Débloqué le {new Date(trophy.unlockedAt).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trophées verrouillés */}
              {lockedTrophies.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    🔒 En cours ({lockedTrophies.length})
                  </h4>
                  <div className="space-y-3">
                    {lockedTrophies.map((trophy) => (
                      <div
                        key={trophy.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg opacity-75"
                      >
                        <div className="text-2xl grayscale">{trophy.icon}</div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-600 dark:text-gray-400">
                            {trophy.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-500">
                            {trophy.description}
                          </div>
                          {trophy.progress !== undefined && trophy.maxProgress && (
                            <div className="mt-2">
                              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span>Progression</span>
                                <span>{trophy.progress}/{trophy.maxProgress}</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${(trophy.progress / trophy.maxProgress) * 100}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucun trophée */}
              {trophies.length === 0 && (
                <div className="text-center py-6">
                  <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Aucun trophée disponible pour le moment
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
